import { EventEmitter } from 'events';

import { DataTableColumns, DataTableRow } from './DataTableTypes.js';

export type DataTableLayoutManagerEvents = {
  update: [];
};

export interface BBoxRect {
  height: number;
  width: number;
  x: number;
  y: number;
}

export class DataTableLayoutManager<
  TRow extends DataTableRow,
  TContext = unknown
> extends EventEmitter<DataTableLayoutManagerEvents> {
  private cellRefs: (HTMLElement | null)[][] = [];
  private columns: DataTableColumns<TRow, TContext> = [];
  private containerWidth: number = 0;

  updateColumns = (columns: DataTableColumns<TRow, TContext>) => {
    this.columns = columns;
  };

  updateContainerWidth = (containerWidth: number) => {
    this.containerWidth = containerWidth;
  };

  /**
   * Unregister a cell ref for a certain column and row this prevent memory leaks and unnecessary renders
   */
  register = (columnIdx: number, rowIdx: number, ref: HTMLElement) => {
    if (!this.cellRefs[columnIdx]) {
      this.cellRefs[columnIdx] = [];
    }
    this.cellRefs[columnIdx][rowIdx] = ref;
  };

  unregister = (columnIdx: number, rowIdx: number) => {
    if (!this.cellRefs[columnIdx]) {
      return;
    }
    this.cellRefs[columnIdx][rowIdx] = null;
  };

  getCellBBox = (rowIdx: number, columnIdx: number): BBoxRect => {
    if (this.cellRefs[columnIdx] && this.cellRefs[columnIdx][rowIdx]) {
      return this.cellRefs[columnIdx][rowIdx]!.getBoundingClientRect();
    }

    return {
      height: 0,
      width: 0,
      x: 0,
      y: 0,
    };
  };

  /**
   * Get the dimensions of a cell at a certain row and column with max/min width/height constraints applied
   *
   * @param rowIdx
   * @param columnIdx
   */
  getCellDimensions = (rowIdx: number, columnIdx: number) => {
    const { height, width } = this.getCellBBox(rowIdx, columnIdx);
    const columnDef = this.columns[columnIdx];
    const maxWidth = columnDef.maxWidth ?? Infinity;
    const minWidth = columnDef.minWidth ?? 0;
    // Todo implement maxHeight and minHeight for rows
    const maxHeight = Infinity;
    const minHeight = 0;

    // width = pixel/fit-content/max-content/min-content
    // height = pixel/fit-content/max-content/min-content
    return {
      height: height,
      width: width,
      maxWidth,
      minWidth,
      maxHeight,
      minHeight,
    };
  };

  baseGetColumnWidth = (columnIdx: number): number => {
    const {
      minWidth: columnMinWidth = 0,
      maxWidth: columnMaxWidth = Infinity,
      width: columnWidth = null,
    } = this.columns[columnIdx] ?? {};

    if (columnWidth !== null) {
      return columnWidth;
    }
    const cells = this.cellRefs[columnIdx];

    if (!cells) {
      return 0;
    }

    let calcWidth = 0;
    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i];
      if (cell) {
        calcWidth = Math.min(
          columnMaxWidth,
          Math.max(columnMinWidth ?? 0, cell.offsetWidth, calcWidth)
        );
      }
    }

    return calcWidth;
  };

  getTotalColumnWidth = (): number => {
    let total = 0;
    for (let i = 0; i < this.columns.length; i++) {
      total += this.baseGetColumnWidth(i);
    }
    return total;
  };

  getColumnWidth = (columnIdx: number): number => {
    let columnWidth = this.baseGetColumnWidth(columnIdx);
    const totalColumnWidth = this.getTotalColumnWidth();
    const remainingWidth = this.containerWidth - totalColumnWidth;

    if (this.containerWidth > totalColumnWidth && this.columns.length > 0) {
      columnWidth += remainingWidth / this.columns.length;
    }

    return columnWidth;
  };

  getRowHeight = (rowIdx: number): number => {
    let calcHeight = 0;
    for (let i = 0; i < this.cellRefs.length; i++) {
      const cell = this.cellRefs[i][rowIdx];
      if (!cell) {
        continue;
      }
      calcHeight = Math.max(cell.offsetHeight, calcHeight);
    }
    return calcHeight;
  };

  update = () => {
    this.emit('update');
  };

  subscribe = (listener: () => void) => {
    this.on('update', listener);
    return () => {
      this.off('update', listener);
    };
  };
}
